import React, { useEffect, useState } from 'react';

import type { IFormAlertProps } from './FormAlert.types';
import type { TIconVariant } from '@ui-library/Icon/Icon.type';
import { Icon } from '@ui-library/Icon/Icon';
import { IconName } from '@ui-library/Icon/Icon.type';
import { Text } from '@ui-library/Text/Text';

export const FormAlert: React.FC<IFormAlertProps> = ({
  message,
  variant = 'error',
  onDismiss,
  className = '',
  autoDismiss = false,
  autoDismissTimeout = 3000,
}) => {
  const [isVisible, setIsVisible] = useState(true);
  
  useEffect(() => {
    if (autoDismiss && message) {
      const timer = setTimeout(() => {
        setIsVisible(false);
        onDismiss?.();
      }, autoDismissTimeout);
      return () => clearTimeout(timer);
    }
  }, [autoDismiss, autoDismissTimeout, message, onDismiss]);

  if (!message || !isVisible) return null;
  

  // Variant-based styling using design tokens
  const variantStyles = {
    error: {
      container: 'bg-bg-error-secondary border-border-error',
      icon: 'text-fg-error-primary',
      text: 'text-text-error-primary',
      dismissButton: 'text-fg-error-primary hover:bg-bg-error-primary focus:ring-focus-ring-error',
    },
    warning: {
      container: 'bg-bg-warning-secondary border-border-secondary',
      icon: 'text-fg-warning-primary',
      text: 'text-text-warning-primary',
      dismissButton: 'text-fg-warning-primary hover:bg-bg-warning-primary focus:ring-focus-ring',
    },
    success: {
      container: 'bg-bg-success-secondary border-border-secondary',
      icon: 'text-fg-success-primary',
      text: 'text-text-success-primary',
      dismissButton: 'text-fg-success-primary hover:bg-bg-success-primary focus:ring-focus-ring',
    },
    info: {
      container: 'bg-bg-brand-section-subtle border-border-brand-alt',
      icon: 'text-fg-brand-primary',
      text: 'text-text-brand-primary',
      dismissButton: 'text-fg-brand-primary hover:bg-bg-brand-section focus:ring-focus-ring',
    },
  };

  const styles = variantStyles[variant];

  // Check if message contains multiple errors separated by " | "
  const errorMessages = message.includes(' | ') ? message.split(' | ') : [message];
  const hasMultipleErrors = errorMessages.length > 1;

  return (
    <div
      className={`flex items-start gap-bg-v2-md p-bg-v2-lg border rounded-bg-v2-md ${styles.container} ${className}`}
      role="alert"
    >
      {/* Icon */}
      {variant === "error" && <div className="flex-shrink-0">
        <Icon name={IconName.Alert} size="md" className={styles.icon} />
      </div>}

      {variant === "success" && <div className="flex-shrink-0">
        <Icon name={IconName.CheckCircle} size="md" className={styles.icon} inheritColor={true} />
      </div>}

      {/* Message Content */}
      <div className="flex-1">
        {hasMultipleErrors ? (
          <div className="flex flex-col gap-bg-v2-xs">
            <Text variant="text-sm" weight="medium" className={styles.text} as="p">
              Please fix the following errors:
            </Text>
            <ul className="list-disc list-inside flex flex-col gap-bg-v2-xxs">
              {errorMessages.map((msg, index) => (
                <li key={index}>
                  <Text variant="text-sm" weight="regular" className={styles.text} as="span">
                    {msg}
                  </Text>
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <Text variant="text-sm" weight="medium" className={styles.text} as="p">
            {message}
          </Text>
        )}
      </div>

      {/* Dismiss Button */}
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          className={`flex-shrink-0 inline-flex items-center justify-center p-bg-v2-xs rounded-bg-v2-sm transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 ${styles.dismissButton}`}
          aria-label="Dismiss alert"
        >
          <Icon name={IconName.Close} size="sm" variant={variant as TIconVariant} />
        </button>
      )}
    </div>
  );
};

